/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiUrl } from '../utilities/apiUrl';
import { differenceInSeconds, isBefore, isAfter } from 'date-fns';
import { styled } from '@mui/material/styles';
import { Typography, TextField, Box } from '@mui/material';
import {
  CheckIn,
  Timesheet,
  CutOff,
  UserInfo,
  GeneralModal,
  ModalButton,
  DashboardGrid,
  GridContainer,
  ClockInMessageModal,
  WorkSetupModal,
  NewEmpModal,
  ConsentModal,
  PhClock,
  ReminderPopupModal,
} from '../components/index';
import * as workerTimers from 'worker-timers';
import '../css/Dashboard.css';
import { useAlert } from '../providers/AlertContext';
import { mapWorkSetup } from './workSetupMap';
import { getSessionLunchWindow, isWithinLunch } from '../utilities/lunchWindow';

const CssTextField = styled(TextField)({
  '& label.Mui-focused': {
    color: '#84d8c5',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#84d8c5',
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: '#84d8c5',
    },
  },
});

export const Dashboard = () => {
  const [empId] = useOutletContext();
  const [emp, setEmp] = useState({});
  const [deptName, setDeptName] = useState('');
  const [deptId, setDeptId] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isLunch, setIsLunch] = useState(false);
  const countRef = useRef(null);
  const [workModalOpen, setWorkModalOpen] = useState(false);
  const [workDescOpen, setWorkDescOpen] = useState(false);
  const [workDesc, setWorkDesc] = useState('');
  const [timer, setTimer] = useState(0);
  const [checkInTime, setCheckInTime] = useState(null);
  const [activeWorkSetup, setActiveWorkSetup] = useState(1);
  const [activeLunchStart, setActiveLunchStart] = useState(null);
  const [fullName, setFullName] = useState('');
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [workSetupModalOpen, setWorkSetupModalOpen] = useState(false);
  const [workSetupResponse, setWorkSetupResponse] = useState(null);
  const [selectedWorkSetup, setSelectedWorkSetup] = useState('');
  const [isSubmittingWorkSetup, setIsSubmittingWorkSetup] = useState(false);
  const [isNewEmployee, setIsNewEmployee] = useState(false);
  const [isNewEmpModalOpen, setIsNewEmpModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [reminderPopupOpen, setReminderPopupOpen] = useState(false);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [reminderBatchKey, setReminderBatchKey] = useState(0);

  const btnTimer = useRef();
  // Holds what to do once the current reminder popup (if any) is confirmed,
  // e.g. proceeding with a checkout that's gated behind a BEFORE_CHECKOUT reminder.
  const reminderContinueRef = useRef(() => {});

  // CLEAR LOADING TIMEOUT
  useEffect(() => {
    return () => {
      clearTimeout(btnTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!isNewEmployee) return;

    const deletePendingEditRequest = async () => {
      try {
        const res = await fetch(
          apiUrl(`/eis/empeditrequest?empid=${empId}&status=PENDING`),
          {
            method: 'DELETE',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const result = await res.json();
        if (result.success) {
          showAlert(
            'The previously created pending edit request was deleted due to a new consent form version.',
            'info',
            'Request Deleted'
          );
        }
      } catch (err) {
        console.error('Failed to delete pending edit request:', err);
      }
    };

    deletePendingEditRequest();
  }, [isNewEmployee]);

  // For logging out
  const handleLogout = (e) => {
    fetch(apiUrl('/auth/logout'), {
      method: 'POST',
      credentials: 'include',
    })
      .then((response) => {
        window.location.replace('/');
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // GET EMPLOYEE DATA AND INITIAL CONSENT
  useEffect(() => {
    fetch(apiUrl('/employee/' + empId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setEmp(data.data);
        setDeptId(data.data.deptid);
        setIsActive(data.data.active);
        setFullName(`${data.data.firstname} ${data.data.lastname}`);
      });

    // GET THEIR INITIAL CONSENT
    fetch(apiUrl('/eis/consentform/' + empId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => {
        if (response.status === 404) {
          setIsNewEmployee(true);
        }
      })
      .catch((err) => console.error('Error fetching consent:', err));
  }, [empId, refreshTrigger]);

  // GET USER DEPARTMENT DATA
  useEffect(() => {
    fetch(apiUrl('/department/' + deptId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setDeptName(data.data.deptname);
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptName]);

  // GET CHECK IN TIME
  useEffect(() => {
    fetch(apiUrl('/timesheet?empid=' + empId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.data[data.data.length - 1]['checkout'] === null) {
          const row = data.data[data.data.length - 1];
          setCheckInTime(new Date(row['checkin']));
          setActiveWorkSetup(row['worksetup']);
          setActiveLunchStart(row['lunchstart'] || null);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [empId]);

  // LUNCH BREAK
  useEffect(() => {
    const currTimer = workerTimers.setInterval(() => {
      //Lunch break (12:00PM = 12:59PM, or the Deployed session's lunch window)
      const { lunchStart, lunchEnd } = getSessionLunchWindow(
        checkInTime || new Date(),
        activeWorkSetup,
        activeLunchStart
      );
      setIsLunch(isWithinLunch(new Date(), lunchStart, lunchEnd));
    }, 1000);
    return () => {
      workerTimers.clearInterval(currTimer);
    };
  }, [isLunch, activeWorkSetup, activeLunchStart, checkInTime]);

  // TIMER
  useEffect(() => {
    const { lunchStart, lunchEnd } = getSessionLunchWindow(
      checkInTime || new Date(),
      activeWorkSetup,
      activeLunchStart
    );

    if (isActive && !isLunch) {
      countRef.current = workerTimers.setInterval(() => {
        setTimer(
          isBefore(checkInTime, lunchStart) && isAfter(new Date(), lunchEnd) // check in before lunch
            ? differenceInSeconds(new Date(), checkInTime) -
                differenceInSeconds(lunchEnd, lunchStart)
            : lunchStart <= checkInTime &&
                lunchEnd >= checkInTime &&
                isAfter(new Date(), lunchEnd) // check in during lunch
              ? differenceInSeconds(new Date(), checkInTime) -
                differenceInSeconds(lunchEnd, checkInTime)
              : differenceInSeconds(new Date(), checkInTime) // check in after lunch
        );
      }, 1000);
      return () => {
        workerTimers.clearInterval(countRef.current);
      };
    }
  }, [
    isActive,
    isLunch,
    timer,
    checkInTime,
    countRef,
    activeWorkSetup,
    activeLunchStart,
  ]);

  // RESET TIMER ON STOP WORK CLICK
  const handleReset = () => {
    clearTimeout(countRef.current);
    setWorkModalOpen(false);
    setTimer(0);
  };

  // STOP WORK CONFIRMATION MODAL
  const handleWorkDescClose = () => {
    setWorkDescOpen(false);
    setWorkModalOpen(false);
    window.location.reload();
  };

  // FORMAT TIMER
  const formatTime = () => {
    const getSeconds = `0${timer % 60}`.slice(-2);
    const minutes = `${Math.floor(timer / 60)}`;
    const getMinutes = `0${minutes % 60}`.slice(-2);

    const hours = Math.floor(timer / 3600);
    let getHours;
    if (hours >= 100) {
      getHours = `0${hours}`.slice(-3);
    } else {
      getHours = `0${hours}`.slice(-2);
    }

    const time = [getHours, getMinutes, getSeconds];

    return time;
  };

  // HANDLE LOGGING
  const handleLog = (state) => {
    fetch(apiUrl('/logger'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        empid: emp._id,
        logState: state,
        time: Date.now(),
      }),
    })
      .then((response) => {
        response.json();
      })
      .then((data) => {
        console.log(data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // FETCH PENDING REMINDERS FOR A GIVEN TRIGGER; SHOW POPUP IF ANY, ELSE CONTINUE
  const fetchAndShowReminders = (trigger, onDone) => {
    fetch(apiUrl(`/reminder/pending?trigger=${trigger}`), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success && data.data.length > 0) {
          setPendingReminders(data.data);
          setReminderBatchKey((key) => key + 1);
          reminderContinueRef.current = onDone;
          setReminderPopupOpen(true);
        } else {
          onDone();
        }
      })
      .catch((err) => {
        console.log(err);
        // Don't let a reminder-service hiccup block check-in/check-out.
        onDone();
      });
  };

  // CONFIRM A SINGLE REMINDER
  const handleConfirmReminderOne = (reminder) => {
    return fetch(apiUrl(`/reminder/${reminder._id}/confirm`), {
      method: 'POST',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data.success) {
          throw new Error('Failed to confirm reminder.');
        }
        return data;
      });
  };

  // ALL REMINDERS IN THE CURRENT POPUP HAVE BEEN CONFIRMED
  const handleAllRemindersConfirmed = () => {
    setReminderPopupOpen(false);
    const continueAction = reminderContinueRef.current;
    reminderContinueRef.current = () => {};
    continueAction();
  };

  // HANDLE LOADING BUTTON
  const handleLoading = () => {
    if (!btnLoading) {
      setBtnLoading(true);
      btnTimer.current = setTimeout(() => {
        setBtnLoading(false);
      }, 2000);
    }
  };

  // CHECK IN / START WORK HANDLER
  const handleCheckIn = (e) => {
    handleLoading();
    e.preventDefault();
    handleFindMessage(empId);
    // SHOW WORK MODAL FIRST BEFORE CHECKING IN
    setWorkSetupModalOpen(true);
  };

  // WORK SETUP CONFIRMATION HANDLER
  const handleWorkSetupConfirm = async (workSetupStr, lunchStart) => {
    // Prevent double submission
    if (isSubmittingWorkSetup) {
      return;
    }

    setIsSubmittingWorkSetup(true);

    try {
      const workSetup = mapWorkSetup(workSetupStr);

      // CREATE A NEW TIMESHEET WITH WORK SETUP INFO
      const response = await fetch(apiUrl('/timesheet/check-in'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          empid: emp._id,
          worksetup: workSetup,
          ...(workSetup === 3 ? { lunchstart: lunchStart } : {}),
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsActive(true);
        setWorkSetupModalOpen(false);
        setWorkSetupResponse({
          type: 'success',
          text: data.message,
        });

        // Set check in time
        const timesheetResponse = await fetch(
          apiUrl('/timesheet?empid=' + empId),
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        const timesheetData = await timesheetResponse.json();

        // Get check in time for the timer computation
        if (
          timesheetData.data[timesheetData.data.length - 1]['checkout'] === null
        ) {
          const row = timesheetData.data[timesheetData.data.length - 1];
          setCheckInTime(new Date(row['checkin']));
          setActiveWorkSetup(row['worksetup']);
          setActiveLunchStart(row['lunchstart'] || null);
        }

        handleLog('start');
        fetchAndShowReminders('AFTER_CHECKIN', () => {});
      } else {
        throw new Error(data.message || 'Failed to check in');
      }
    } catch (err) {
      console.error(err);

      // ASSIGN RESPECTIVE ERROR MESSAGE DEPENDING ON WORK SETUP
      let errorText = err.message || 'Failed to check in. Please try again.';
      const workSetup = mapWorkSetup(workSetupStr);

      if (workSetup === 1) {
        errorText = 'Earliest time in is 06:00 AM. Please try again later.';
      } else if (workSetup === 2) {
        errorText = 'Earliest time in is 08:00 AM. Please try again later.';
      } else {
        errorText = err.message || 'Check in failed. Please try again.';
      }

      setWorkSetupResponse({
        type: 'error',
        text: errorText,
      });
    } finally {
      // Reset submitting state after a delay
      setTimeout(() => {
        setIsSubmittingWorkSetup(false);
      }, 1000);
    }
  };

  // WORK SETUP MODAL CANCELLATION HANDLER
  const handleWorkSetupCancel = () => {
    setWorkSetupModalOpen(false);
    setWorkSetupResponse(null);
    setSelectedWorkSetup('');
    setIsSubmittingWorkSetup(false);
  };

  // CHECK OUT / END WORK HANDLER
  const handleCheckOut = (e) => {
    e.preventDefault();

    const workdesc = workDesc;

    const performCheckOut = () => {
      setCheckInTime(null);

      fetch(apiUrl('/timesheet/check-out'), {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          empid: emp._id,
          workdesc: workdesc,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          handleReset();
          setIsActive(false);
          setWorkDescOpen(false);
          // reset states of work setup
          setSelectedWorkSetup('');
          setWorkSetupResponse(null);
          // Reload is deferred until any AFTER_CHECKOUT reminder is confirmed.
          fetchAndShowReminders('AFTER_CHECKOUT', () => {
            window.location.reload();
          });
        })
        .catch((err) => {
          console.log(err);
        });

      handleLog('descOkay');
    };

    fetchAndShowReminders('BEFORE_CHECKOUT', performCheckOut);
  };

  // GET ALL THE MESSAGES OF THE USER FROM DB
  const handleFindMessage = (empId) => {
    fetch(apiUrl('/message'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          // get all the messages of the user
          const messageData = data.data.filter((msg) => msg.empid === empId);

          // get id of last message
          const ids = messageData.map((msg) => msg._id);
          const id = ids[ids.length - 1];
          const lastMessageData = messageData[messageData.length - 1];

          const numviews = messageData.map((msg) => msg.numviews);

          // check if numviews is not 0
          if (numviews[numviews.length - 1] === 0) {
            setMessageModalOpen(false);
          } else {
            setMessages(messageData.map((msg) => msg.message));
            setSubjects(messageData.map((msg) => msg.subject));
            subtractNumViews(lastMessageData, numviews[numviews.length - 1]);
            setMessageModalOpen(true);
          }
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // SUBTRACT NUMVIEWS WHEN USER VIEWS THE MESSAGE/CLOCK INS
  const subtractNumViews = (messageData, numviews) => {
    fetch(apiUrl('/message/' + messageData._id), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        empid: messageData.empid,
        subject: messageData.subject,
        message: messageData.message,
        postedby: messageData.postedby,
        dateposted: messageData.dateposted,
        numviews: numviews - 1,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data.message);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const { showAlert } = useAlert();

  return (
    <GridContainer selectedPage={0}>
      <DashboardGrid md={7}>
        <CutOff isActive={isActive} />
      </DashboardGrid>

      <DashboardGrid md={5}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          paddingTop={3}
          flexDirection="column"
          gap={2}
          width={'100%'}
        >
          <PhClock />
          <CheckIn
            isActive={isActive}
            clickHandler={
              isActive
                ? () => {
                    setWorkModalOpen(true);
                    handleLog('stop');
                  }
                : handleCheckIn
            }
            timer={formatTime()}
            modalOpen={workModalOpen}
            modalClose={() => {
              setWorkModalOpen(false);
            }}
            stopWork={() => {
              setWorkDescOpen(true);
              handleLog('workModalStop');
            }}
            cancel={() => {
              setWorkModalOpen(false);
              handleLog('workModalCancel');
              window.location.reload();
            }}
            isLunch={isLunch}
            loading={btnLoading}
          />
        </Box>
      </DashboardGrid>
      <DashboardGrid md={5}>
        <UserInfo
          fullName={fullName}
          username={emp.username}
          phone={emp.cellphone}
          role={emp.role}
          email={emp.email}
          deptname={deptName}
          country={emp.country}
          lastProj={emp.lastproject}
        />
      </DashboardGrid>
      <DashboardGrid md={7}>
        <Timesheet isActive={isActive} />
      </DashboardGrid>

      {/* Work Description Modal */}
      <GeneralModal open={workDescOpen} onClose={handleWorkDescClose}>
        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h2"
          gutterBottom
        >
          Work Description
        </Typography>
        <CssTextField
          fullWidth
          size="small"
          label="Enter work description or comments"
          variant="outlined"
          margin="normal"
          onChange={(e) => {
            setWorkDesc(e.target.value);
          }}
        />
        <ModalButton
          primaryClickHandler={handleCheckOut}
          secondaryClickHandler={() => {
            handleWorkDescClose();
            handleLog('descCancel');
          }}
          primaryBtnName="Okay"
          secondaryBtnName="Cancel"
        />
      </GeneralModal>

      {/* WORK SETUP MODAL */}
      <WorkSetupModal
        open={workSetupModalOpen}
        selectedWorkSetup={selectedWorkSetup}
        setSelectedWorkSetup={setSelectedWorkSetup}
        onConfirm={handleWorkSetupConfirm}
        response={workSetupResponse}
        onCancel={handleWorkSetupCancel}
      />
      {/* <ClockInMessageModal
        open={messageModalOpen}
        onClose={() => {
          setMessageModalOpen(false);
        }}
        subject={subjects[subjects.length - 1]}
        message={messages[messages.length - 1]}
      /> */}

      <ReminderPopupModal
        key={reminderBatchKey}
        open={reminderPopupOpen}
        reminders={pendingReminders}
        onConfirmOne={handleConfirmReminderOne}
        onAllConfirmed={handleAllRemindersConfirmed}
      />

      {isNewEmployee && (
        <ConsentModal
          openNextModal={() => {
            setIsNewEmpModalOpen(true);
          }}
          open={isNewEmployee}
          secondaryClick={handleLogout}
        />
      )}

      <NewEmpModal
        role={emp.role}
        empid={empId}
        lastname={emp.lastname}
        firstname={emp.firstname}
        username={emp.username}
        middlename={emp.middlename}
        email={emp.email}
        open={isNewEmpModalOpen}
        onClose={() => {
          setIsNewEmpModalOpen(false);
          setIsNewEmployee(false);
        }}
        onSuccess={() => setRefreshTrigger((c) => c + 1)}
      />
    </GridContainer>
  );
};
